import { Line } from 'react-chartjs-2'
import { Chart as ChartJS, Filler, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend } from 'chart.js'
import type { MoneyPoint } from '@/api/types'

// Filler is required for stacked-area fills; the others are the line-chart essentials.
ChartJS.register(Filler, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend)

// Compact currency for axis/tooltip (e.g. "$1.2M").
function compact(value: number, currency?: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value)
  } catch {
    return new Intl.NumberFormat(undefined, { notation: 'compact' }).format(value)
  }
}

// Loss clusters, stacked bottom→top with a stable color each.
const CLUSTERS: { key: 'outage' | 'data' | 'regulatory'; label: string; color: string; fill: string }[] = [
  { key: 'outage', label: 'Outage', color: '#254F71', fill: 'rgba(37,79,113,0.85)' },
  { key: 'data', label: 'Data loss', color: '#e8734f', fill: 'rgba(232,115,79,0.85)' },
  { key: 'regulatory', label: 'Regulatory', color: '#eab308', fill: 'rgba(234,179,8,0.85)' },
]

export function MoneyTrendLine({ points, currency }: { points: MoneyPoint[]; currency?: string }) {
  const labels = points.map((p) =>
    new Date(p.taken_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  )
  // If snapshots predate cluster capture, fall back to a single total series.
  const hasClusters = points.some((p) => p.outage || p.data || p.regulatory)
  const datasets = hasClusters
    ? CLUSTERS.map((c) => ({
        label: c.label,
        data: points.map((p) => p[c.key]),
        borderColor: c.color,
        backgroundColor: c.fill,
        fill: true,
        stack: 'exposure',
        tension: 0.3,
        pointRadius: 0,
      }))
    : [
        {
          label: '$-at-risk',
          data: points.map((p) => p.value),
          borderColor: '#254F71',
          backgroundColor: 'rgba(37,79,113,0.12)',
          fill: true,
          tension: 0.3,
          pointRadius: 0,
        },
      ]
  return (
    <Line
      data={{ labels, datasets }}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: hasClusters, labels: { boxWidth: 12, font: { size: 11 } } },
          tooltip: {
            callbacks: {
              label: (ctx) => `${ctx.dataset.label}: ${compact(ctx.parsed.y ?? 0, currency)}`,
              footer: hasClusters
                ? (items) => `Total: ${compact(items.reduce((s, i) => s + (i.parsed.y ?? 0), 0), currency)}`
                : undefined,
            },
          },
        },
        scales: {
          y: {
            stacked: hasClusters,
            beginAtZero: true,
            ticks: { callback: (v) => compact(Number(v), currency) },
          },
        },
      }}
    />
  )
}
